import { useState } from 'react';
import { Search, Eye, CheckCircle, AlertTriangle, XCircle, MoreVertical, Flag } from 'lucide-react';

const LISTINGS = [
  { id: 1, title: 'Mercedes C-Class 2022', seller: 'Karimi Motors', price: 480000, status: 'active', category: 'Car', posted: 'Jun 1, 2024', views: 234, flagged: false },
  { id: 2, title: 'BMW X5 2023', seller: 'Atlas Auto', price: 850000, status: 'pending', category: 'SUV', posted: 'Jun 5, 2024', views: 0, flagged: false },
  { id: 3, title: 'Toyota Corolla 2021', seller: 'Mohammed A.', price: 185000, status: 'flagged', category: 'Car', posted: 'May 28, 2024', views: 89, flagged: true },
  { id: 4, title: 'Suzuki Jimny 2022', seller: 'Fatima Z.', price: 220000, status: 'active', category: 'SUV', posted: 'May 20, 2024', views: 156, flagged: false },
  { id: 5, title: 'Honda CB500 2021', seller: 'Youssef K.', price: 55000, status: 'suspended', category: 'Motorcycle', posted: 'Apr 15, 2024', views: 43, flagged: false },
];

const statusConfig: Record<string, { class: string; icon: any }> = {
  active: { class: 'bg-[#22C55E]/10 text-[#22C55E]', icon: CheckCircle },
  pending: { class: 'bg-[#F59E0B]/10 text-[#F59E0B]', icon: AlertTriangle },
  flagged: { class: 'bg-[#EF4444]/10 text-[#EF4444]', icon: Flag },
  suspended: { class: 'bg-[#717182]/10 text-[#717182]', icon: XCircle },
};

export function VehicleListings() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState<number | null>(null);

  const filtered = LISTINGS.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (search && !l.title.toLowerCase().includes(search.toLowerCase()) && !l.seller.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">Vehicle Listings</h1>
        <p className="text-[#717182]">{LISTINGS.length} total listings</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Total', value: LISTINGS.length, status: 'all' },
          { label: 'Active', value: LISTINGS.filter(l => l.status === 'active').length, status: 'active' },
          { label: 'Pending', value: LISTINGS.filter(l => l.status === 'pending').length, status: 'pending' },
          { label: 'Flagged', value: LISTINGS.filter(l => l.status === 'flagged').length, status: 'flagged' },
          { label: 'Suspended', value: LISTINGS.filter(l => l.status === 'suspended').length, status: 'suspended' },
        ].map(s => (
          <button key={s.status} onClick={() => setStatusFilter(s.status)}
            className={`p-3 rounded-xl text-center transition-colors ${statusFilter === s.status ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#2D323A] hover:border-[#1E6DFF]'}`}>
            <p className="text-xl">{s.value}</p>
            <p className="text-xs">{s.label}</p>
          </button>
        ))}
      </div>

      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search listings..." className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB] text-xs text-[#717182]">
          <span>Listing</span><span>Seller</span><span>Category</span><span>Price</span><span>Views</span><span>Status</span><span>Actions</span>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {filtered.map(l => {
            const sc = statusConfig[l.status];
            const StatusIcon = sc.icon;
            return (
              <div key={l.id} className="flex flex-col md:grid md:grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 items-start md:items-center px-5 py-4">
                <div>
                  <p className="text-sm text-[#2D323A]">{l.title}</p>
                  <p className="text-xs text-[#717182]">Posted {l.posted}</p>
                </div>
                <span className="text-sm text-[#717182]">{l.seller}</span>
                <span className="text-xs px-2 py-0.5 bg-[#F5F7FA] text-[#717182] rounded-lg">{l.category}</span>
                <span className="text-sm text-[#2D323A]">{l.price.toLocaleString()} MAD</span>
                <span className="text-sm text-[#717182] flex items-center gap-1"><Eye className="w-3 h-3" />{l.views}</span>
                <span className={`text-xs px-2 py-0.5 rounded-lg flex items-center gap-1 ${sc.class}`}>
                  <StatusIcon className="w-3 h-3" />{l.status}
                </span>
                <div className="relative">
                  <button onClick={() => setOpenMenu(openMenu === l.id ? null : l.id)} className="p-2 hover:bg-[#F5F7FA] rounded-xl">
                    <MoreVertical className="w-4 h-4 text-[#717182]" />
                  </button>
                  {openMenu === l.id && (
                    <div className="absolute right-0 mt-1 w-36 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-1 z-10">
                      <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA] w-full"><Eye className="w-4 h-4" /> View</button>
                      {l.status === 'pending' && <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#22C55E] hover:bg-[#F5F7FA] w-full"><CheckCircle className="w-4 h-4" /> Approve</button>}
                      {l.status === 'flagged' && <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#22C55E] hover:bg-[#F5F7FA] w-full"><CheckCircle className="w-4 h-4" /> Clear Flag</button>}
                      <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#EF4444] hover:bg-[#F5F7FA] w-full"><XCircle className="w-4 h-4" /> Remove</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
